// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import { AuthContext, AuthContextType } from '@/contexts/AuthContext';

interface UserDashboard {
  role_slug: string;
  role_name: string;
  dashboard_path: string;
}

export const useUserDashboards = () => {
  // Safely access auth context - may not be available during password reset flows
  const authContext = useContext(AuthContext) as AuthContextType | undefined;
  const profile = authContext?.profile ?? null;
  
  const [dashboards, setDashboards] = useState<UserDashboard[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchUserDashboards = async () => {
      if (!profile?.id) return;

      try {
        setIsLoading(true);
        
        // RLevents migration: role_assignments table was dropped — return empty.
        const roleAssignments: any[] = [];
        const error = null;

        if (error) {
          console.error('Error fetching user dashboards:', error);
        }

        // Check for staff/admin roles in user_roles table
        const STAFF_ROLES = ['admin', 'org_admin', 'rlevents_super_admin', 'rlevents_tenant_admin', 'rlevents_tenant_staff', 'staff'];
        const { data: staffRoleRows } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', profile.id)
          .in('role', STAFF_ROLES);

        const hasStaffRole = (staffRoleRows || []).length > 0;

        // Also check if user is an active event staff member
        const { data: eventStaffRow } = await supabase
          .from('event_staff')
          .select('id')
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .maybeSingle();

        const isEventStaff = !!eventStaffRow;

        console.log('🔍 useUserDashboards - Staff checks:', { hasStaffRole, isEventStaff });

        // Transform role assignments data
        const roleBasedDashboards: UserDashboard[] = (roleAssignments || [])
          .map(assignment => {
            const role = Array.isArray(assignment.roles) ? assignment.roles[0] : assignment.roles;
            let dashboardPath = role?.dashboard_path || '';
            
            // Map old vendor-dashboard paths to new vendor-press-dashboard paths
            if (dashboardPath === '/vendor-dashboard' || dashboardPath.startsWith('/vendor-dashboard/')) {
              console.log('🔄 Mapping old vendor-dashboard to vendor-press-dashboard');
              dashboardPath = '/vendor-press-dashboard';
            }
            
            return {
              role_slug: assignment.role_slug,
              role_name: role?.name || '',
              dashboard_path: dashboardPath
            };
          })
          .filter(dashboard => dashboard.dashboard_path && dashboard.role_name);

        // Add primary role dashboard as fallback if not already in role assignments
        const primaryRoleDashboards: UserDashboard[] = [];
        
        // Map profile roles to their dashboard paths
        const roleDashboardMap: Record<string, { name: string; path: string }> = {
          'athlete': { name: 'Athlete', path: '/athlete-dashboard' },
          'admin': { name: 'Admin', path: '/admin-dashboard' },
          'club_coach': { name: 'Club', path: '/club-dash' },
          'college_coach': { name: 'College Coach', path: '/college-coach-dashboard' },
          'vendor': { name: 'Press Vendor', path: '/vendor-press-dashboard' },
          'video_vendor': { name: 'Video Vendor', path: '/vendor-video-dashboard' },
          'affiliate': { name: 'Affiliate', path: '/affiliate-dashboard' },
          'team_member': { name: 'Team', path: '/team-dashboard' },
        };
        
        if (profile.role && roleDashboardMap[profile.role]) {
          const primaryDashboard = roleDashboardMap[profile.role];
          
          // Only add if not already in role-based dashboards
          const alreadyExists = roleBasedDashboards.some(
            d => d.dashboard_path === primaryDashboard.path
          );
          
          if (!alreadyExists) {
            primaryRoleDashboards.push({
              role_slug: profile.role,
              role_name: primaryDashboard.name,
              dashboard_path: primaryDashboard.path
            });
          }
        }

        // Add org_admin dashboard if user has that role
        const orgAdminDashboards: UserDashboard[] = [];
        if ((staffRoleRows || []).some((r: any) => r.role === 'org_admin')) {
          orgAdminDashboards.push({
            role_slug: 'org_admin',
            role_name: 'Organization Admin',
            dashboard_path: '/org-admin-dashboard'
          });
        }

        // Add Staff Portal dashboard if user has any staff/admin role or is an active event staff member
        const staffDashboards: UserDashboard[] = [];
        if (hasStaffRole || isEventStaff) {
          staffDashboards.push({
            role_slug: 'staff',
            role_name: 'Staff Portal',
            dashboard_path: '/staff'
          });
        }

        // Combine and sort all dashboards
        const allDashboards = [...primaryRoleDashboards, ...roleBasedDashboards, ...orgAdminDashboards, ...staffDashboards]
          .sort((a, b) => a.role_name.localeCompare(b.role_name));

        console.log('📊 useUserDashboards - Final dashboard list:', allDashboards);
        setDashboards(allDashboards);
      } catch (error) {
        console.error('Error in fetchUserDashboards:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDashboards();
  }, [profile?.id, profile?.role]);

  return {
    dashboards,
    isLoading,
    hasMultipleDashboards: dashboards.length > 1
  };
};
